import React, { useMemo } from "react";
import type { PayslipConfig, PayslipElement } from "@/lib/payslipConfig";
import { applyPayslipPlaceholders } from "@/lib/payslipConfig";
import { formatDate } from "@/lib/dates";

/* A live, sample-data preview of how the configured driver summary +
   payslip will look. Uses fixed demo turnover so admins can judge the
   layout, columns and branding without needing real driver data.

   This mirrors src/lib/payslip.ts's real rendering rules exactly (same
   two modes -- elements-driven when blocks are configured, the old fixed
   layout otherwise) so what's shown here is what actually prints. */

const money = (n: number) => `$${(Number(n || 0)).toFixed(2)}`;

/* Demo turnover per source column (GST-inclusive) used for the preview. */
const SAMPLE_AMOUNTS: Record<string, number> = {
  cash_amt: 120,
  eftpos_amt: 240,
  totalmobility_amt: 180,
  tm_hoist_amt: 46,
  account_amt: 90,
  ecard_amt: 365.24,
  discount: 15,
  other_amt: 20,
};

/* Demo driver-payable % per payment type for the preview commission calc. */
const SAMPLE_PERCENT: Record<string, number> = {
  Cash: 50,
  EFTPOS: 50,
  "TM Subsidy": 50,
  "TM Hoist": 50,
  Account: 50,
  "e-card": 50,
  Discount: 100,
};

const GST_RATE = 0.15;
const FLAT_PERCENT = 60;
const SAMPLE_BONUS = 35;
const SAMPLE_INSURANCE = 15;

interface Props {
  config: PayslipConfig;
  driverName?: string;
}

const PayslipPreview: React.FC<Props> = ({ config, driverName = "Brian (sample)" }) => {
  const accent = config.branding.accentColor || "#16a34a";

  const { visibleCols, gross, gstAmount, netTake, commission, driverPayment, total } =
    useMemo(() => {
      const visible = config.columns.filter((c) => c.visible);
      let g = 0;
      let driverPortion = 0;
      visible.forEach((c) => {
        const colGross = SAMPLE_AMOUNTS[c.key] ?? 0;
        g += colGross;
        const colExGst = colGross / (1 + GST_RATE);
        const pct = (c.paymentType && SAMPLE_PERCENT[c.paymentType]) ?? FLAT_PERCENT;
        driverPortion += colExGst * (pct / 100);
      });
      const exGst = g / (1 + GST_RATE);
      const gst = g - exGst;
      const comm = Math.max(0, exGst - driverPortion);
      const totalDeductions = SAMPLE_INSURANCE;
      const totalPay = driverPortion + SAMPLE_BONUS - totalDeductions;
      return {
        visibleCols: visible,
        gross: g,
        gstAmount: gst,
        netTake: exGst,
        commission: comm,
        driverPayment: driverPortion,
        total: totalPay,
      };
    }, [config]);

  const elements = config.elements || [];

  /* Sample Mon–Sun week for previewing {week_start}/{week_end} placeholders. */
  const { sampleWeekStart, sampleWeekEnd } = useMemo(() => {
    const fmt = (dt: Date) => formatDate(dt);
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { sampleWeekStart: fmt(monday), sampleWeekEnd: fmt(sunday) };
  }, []);
  const ph = (text: string) =>
    applyPayslipPlaceholders(text, { driverName, weekStart: sampleWeekStart, weekEnd: sampleWeekEnd });

  const alignClass = (align: PayslipElement["align"]) =>
    align === "left" ? "text-left" : align === "right" ? "text-right" : "text-center";
  const justifyClass = (align: PayslipElement["align"]) =>
    align === "left" ? "justify-start" : align === "right" ? "justify-end" : "justify-center";

  const dailyTable = (
    <div className="overflow-x-auto mb-4">
      <table className="w-full border-collapse text-[11px]">
        <thead>
          <tr>
            <th className="border border-zinc-200 bg-zinc-100 px-2 py-1 text-left">Date</th>
            <th className="border border-zinc-200 bg-zinc-100 px-2 py-1 text-left">Jobs</th>
            {visibleCols.map((c) => (
              <th key={c.key} className="border border-zinc-200 bg-zinc-100 px-2 py-1 text-right">
                {c.label}
              </th>
            ))}
            <th className="border border-zinc-200 bg-zinc-100 px-2 py-1 text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="border border-zinc-200 px-2 py-1">Mon</td>
            <td className="border border-zinc-200 px-2 py-1">8</td>
            {visibleCols.map((c) => (
              <td key={c.key} className="border border-zinc-200 px-2 py-1 text-right">
                {money(SAMPLE_AMOUNTS[c.key] ?? 0)}
              </td>
            ))}
            <td className="border border-zinc-200 px-2 py-1 text-right">{money(gross)}</td>
          </tr>
          <tr className="font-bold bg-zinc-50">
            <td className="border border-zinc-200 px-2 py-1">TOTAL</td>
            <td className="border border-zinc-200 px-2 py-1">8</td>
            {visibleCols.map((c) => (
              <td key={c.key} className="border border-zinc-200 px-2 py-1 text-right">
                {money(SAMPLE_AMOUNTS[c.key] ?? 0)}
              </td>
            ))}
            <td className="border border-zinc-200 px-2 py-1 text-right">{money(gross)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  const summaryCard = (
    <div className="mx-auto max-w-sm overflow-hidden rounded-lg border border-zinc-300">
      <div className="px-4 py-2 text-sm font-bold text-white" style={{ background: accent }}>
        Payment Summary — {driverName}
      </div>
      <div className="px-4 py-3 text-sm space-y-1">
        <div className="flex justify-between">
          <span>Gross Turnover</span>
          <span>{money(gross)}</span>
        </div>
        <div className="flex justify-between text-red-600">
          <span>Less GST</span>
          <span>-{money(gstAmount).replace("$", "")}</span>
        </div>
        <div className="flex justify-between font-semibold border-t pt-1">
          <span>Net Take</span>
          <span>{money(netTake)}</span>
        </div>
        <div className="flex justify-between text-red-600">
          <span>Less Commission</span>
          <span>-{money(commission).replace("$", "")}</span>
        </div>
        <div className="flex justify-between font-semibold border-t pt-1">
          <span>Driver Payment</span>
          <span>{money(driverPayment)}</span>
        </div>
        <div className="flex justify-between border-t pt-1" style={{ color: accent }}>
          <span>Bonus</span>
          <span>+{money(SAMPLE_BONUS).replace("$", "")}</span>
        </div>
        <div className="flex justify-between font-semibold border-t pt-1">
          <span>Deductions</span>
          <span>-{money(SAMPLE_INSURANCE).replace("$", "")}</span>
        </div>
        <div className="flex justify-between pl-4 text-xs text-zinc-500">
          <span>Insurance</span>
          <span className="text-red-600">-{money(SAMPLE_INSURANCE).replace("$", "")}</span>
        </div>
        <div
          className="flex justify-between font-bold border-t-2 pt-2 mt-1 text-base"
          style={{ color: accent }}
        >
          <span>Total Payment</span>
          <span>{money(total)}</span>
        </div>
      </div>
    </div>
  );

  const hasSummaryBlock = elements.some((e) => e.type === "summary");

  const renderBlock = (el: PayslipElement) => {
    switch (el.type) {
      case "logo":
        return el.value ? (
          <div key={el.id} className={`flex ${justifyClass(el.align)} mb-2`}>
            <img src={el.value} alt="Logo" className="h-12 max-w-[200px] object-contain" />
          </div>
        ) : (
          <div
            key={el.id}
            className={`${alignClass(el.align)} mb-2 rounded border border-dashed border-zinc-300 py-3 text-xs text-zinc-400`}
          >
            Logo block — add an image URL
          </div>
        );
      case "heading":
        return (
          <div key={el.id} className={`${alignClass(el.align)} mb-1 text-lg font-bold`}>
            {el.value ? ph(el.value) : "Heading"}
          </div>
        );
      case "tagline":
        return (
          <div key={el.id} className={`${alignClass(el.align)} mb-1 text-xs text-zinc-500`}>
            {el.value ? ph(el.value) : "Tagline"}
          </div>
        );
      case "text":
        return (
          <div key={el.id} className={`${alignClass(el.align)} mb-1 text-xs text-zinc-600`}>
            {el.value ? ph(el.value) : "Text line"}
          </div>
        );
      case "footer":
        return (
          <div key={el.id} className={`${alignClass(el.align)} mb-1 text-xs text-zinc-500`}>
            {el.value ? ph(el.value) : "Footer text"}
          </div>
        );
      case "spacer":
        return <div key={el.id} className="h-3" />;
      case "daily_table":
        return <div key={el.id}>{dailyTable}</div>;
      case "summary":
        return (
          <div key={el.id} className="my-2">
            {summaryCard}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="rounded-xl border border-border bg-white text-zinc-900 p-5 shadow-inner">
      {elements.length > 0 ? (
        <>
          <div className="mb-2 space-y-1">{elements.map(renderBlock)}</div>
          <div className="text-center text-xs text-zinc-500 mb-4">{driverName}</div>
        </>
      ) : (
        <>
          {/* Legacy fallback -- shown only for a config saved before blocks
              existed, matching payslip.ts's own fallback exactly. */}
          <div className="text-center mb-4">
            {config.branding.logoUrl ? (
              <img
                src={config.branding.logoUrl}
                alt="Logo"
                className="mx-auto mb-2 h-12 max-w-[200px] object-contain"
              />
            ) : null}
            <h2 className="text-lg font-bold">
              {config.branding.companyName || "Weekly Payment Summary"}
            </h2>
            <div className="text-xs text-zinc-500">{driverName}</div>
            {config.branding.tagline ? (
              <div className="text-xs text-zinc-500 mt-0.5">{config.branding.tagline}</div>
            ) : null}
          </div>
          {config.branding.showDailyBreakdown && dailyTable}
        </>
      )}

      {!hasSummaryBlock && summaryCard}

      <p className="mt-3 text-center text-[11px] text-zinc-400">
        Preview uses sample turnover &amp; rates. Real figures come from each driver&apos;s jobs.
      </p>
    </div>
  );
};

export default PayslipPreview;
